/**
 * List-of-Documents table insertion for the docx editor (MDRS-98).
 *
 * The List-of-Documents-Provided module's source data is two strings:
 *   • `summary_from_doc_sorter` — a numbered list of documents emitted
 *     by `handler.py`'s `get_user_initial_answers` (`documents_provided`
 *     branch), e.g.
 *
 *       "I confirm I received the medical reports and other material…
 *
 *        Letter of Instruction:
 *        1. Smith Lawyers | LOI from Smith Lawyers.pdf, 01/01/2026
 *
 *        Medical Records:
 *        2. GP Clinic | GP records — Jan 2026.pdf, 02/02/2026"
 *
 *     Legacy rows (pre file-name change) used the format
 *     `N. <prefix> from/of <author>, <date>` — the parser falls back to
 *     splitting on " from " / " of " / " by " when the ` | ` separator
 *     is absent, so historical cases still render.
 *
 *   • `experts_response` — optional commentary the expert types
 *     underneath the list (free-text).
 *
 * Render shape:
 *
 *     SUMMARY OF DOCUMENTS PROVIDED        ← preserved template heading,
 *                                            or inserted heading when the
 *                                            template doesn't carry one.
 *
 *     [preamble paragraph(s), if any]
 *
 *     ┌──────────────────┬────────────────────────────┬───────────┐
 *     │ Author           │ Name of document           │ Date      │
 *     ├──────────────────┼────────────────────────────┼───────────┤
 *     │ Smith Lawyers    │ LOI from Smith Lawyers.pdf │ 01/01/2026│
 *     │ GP Clinic        │ GP records — Jan 2026.pdf  │ 02/02/2026│
 *     └──────────────────┴────────────────────────────┴───────────┘
 *
 *     [experts_response paragraphs, one per line]
 *
 * Template-heading detection: case-insensitive, trailing-colon-tolerant
 * match against `LIST_OF_DOCUMENTS_TEMPLATE_HEADING_ALIASES`. When the
 * matched heading already has body content (any non-blank, non-heading
 * paragraph until the next heading or signature), we leave the module
 * alone entirely — the report author already populated this section.
 *
 * Table fallback: the docx-editor schema ships `table`/`tableRow`/
 * `tableCell` nodes (real OOXML tables round-trip via @eigenpal). When
 * those nodes are absent (e.g. unit-test schemas, future fork strips),
 * we emit tab-aligned text paragraphs instead so the data still lands
 * in the doc.
 *
 * Idempotency: when the module's heading is present in the doc with
 * body content (which our own first-fire's table satisfies), insertion
 * is skipped on subsequent fires. Same pattern as `qta-insert.ts`.
 */
import { Fragment, type Node as PMNode } from "prosemirror-model";
import type { EditorView } from "prosemirror-view";

import { buildInlineNodes, firstRunMarks, visualParaAttrs } from "./para-clone";
import { type OutlineHints, isHeadingPara } from "./qta-insert";

export const LIST_OF_DOCUMENTS_TEMPLATE_HEADING_ALIASES = [
  "List of Documents",
  "List of Documents Provided",
  "Documents Provided",
  "Schedule of Documents",
  "Summary of Documents",
  "Summary of Documents Provided",
] as const;

// Matches "N. <document text>" with an optional trailing ", DD/MM/YYYY"
// date — the same format produced by handler.py's `documents_provided`
// branch of `get_user_initial_answers`.
const NUMBERED_LINE_RE =
  /^\s*(\d+)\.\s+(.+?)\s*(?:,\s*(\d{1,2}\/\d{1,2}\/\d{4}))?\s*$/;

const SIGNATURE_RE = /yours\s+(sincerely|faithfully)/i;

export type DocumentRow = {
  // Author/source of the document, e.g. "Smith Lawyers" or "Dr Jones".
  // Backend emits this as the first field of the pipe-delimited
  // "<author> | <name>" line (from `file.author`).
  author: string;
  // Name of the document. New backend rows hold the actual uploaded
  // filename (`file.name`, e.g. "LOI from Smith Lawyers.pdf"). Legacy
  // rows hold the classification-derived prefix ("Letter from" /
  // "Records of") because the file name wasn't being emitted yet.
  document: string;
  date: string;
};

// Document-type acronyms that stay uppercase through title-casing —
// title-casing them (LOI → "Loi") reads wrong. Extend as new ones show up.
const KNOWN_ACRONYMS = new Set([
  "LOI",
  "IME",
  "GP",
  "MRI",
  "CT",
  "CTP",
  "XR",
  "EMG",
  "ECG",
  "EEG",
  "NCS",
  "ABN",
  "NDIS",
  "DVA",
  "WC",
  "MVA",
  "TBI",
  "PTSD",
  "OT",
  "ENT",
]);

// Title-case one whitespace-delimited word, preserving known acronyms and
// title-casing each hyphen-separated part (so "loi-mari" → "LOI-Mari").
const titleCaseWord = (word: string): string =>
  word
    .split("-")
    .map((part) => {
      if (!part) return part;
      if (KNOWN_ACRONYMS.has(part.toUpperCase())) return part.toUpperCase();
      return part.charAt(0).toUpperCase() + part.slice(1).toLowerCase();
    })
    .join("-");

/**
 * Clean a raw uploaded filename into a human-readable document name for the
 * table's "Name of document" column. Applied only to the real-filename path
 * (the pipe-delimited format), not the legacy classification prefix.
 *
 * Rules (see MDRS documents-table cleanup):
 *   1. drop a trailing file extension (`.pdf`, `.docx`, …);
 *   2. drop a leading index (`1.` / `12)`) AND a leading sort-order prefix the
 *      doc-sorter prepends (`01 ` / `08_`, 1–3 digits + space/underscore). A
 *      4-digit leading year ("2022 Annual Report") is kept — only short
 *      ordering numbers are dropped.
 *      TRADEOFF: a content name that legitimately begins with a 1–3 digit number
 *      ("12 Month Review", "6 Minute Walk Test") is syntactically identical to an
 *      ordering prefix and is also stripped. Accepted because (a) the doc-sorter
 *      prefix is the dominant real-world case and (b) the effect is a cosmetic
 *      display-string shortening — the stored file is untouched and the expert can
 *      edit the name in the report. The clean fix is upstream: have the doc-sorter
 *      carry its ordering index as separate metadata instead of concatenating it
 *      into `f.name`, so the display layer never has to guess;
 *   3. drop a TRAILING sequence suffix — only when it is `_`/`-`-delimited and
 *      1–3 digits (`_145`, `-2`), the shape the doc-sorter appends to its
 *      underscore filenames. A SPACE-delimited number ("Bundle 18",
 *      "MRI scan 2024") and any 4-digit year are kept — those are meaningful,
 *      not sequence artifacts;
 *   4. underscores → spaces, collapse whitespace;
 *   5. title-case, preserving known acronyms (LOI, IME, GP, …).
 *
 * `_Bundle 18_MEDICAL_RECORDS_AND_REPORTS_145.pdf` → `Bundle 18 Medical Records And Reports`;
 * `Bundle 18.pdf` → `Bundle 18`; `MRI scan 2024.pdf` → `MRI Scan 2024`.
 */
export const cleanDocumentName = (raw: string): string => {
  let s = (raw ?? "").trim();
  if (!s) return s;
  s = s.replace(/\.[A-Za-z0-9]{1,5}$/, ""); // 1. extension
  s = s.replace(/^\s*\d+\s*[.)]\s*/, ""); // 2a. leading index "1." / "12)"
  s = s.replace(/^\s*\d{1,3}[\s_]+/, ""); // 2b. leading sort-order prefix "01 " / "08_" (doc-sorter ordering); 4-digit years are kept
  s = s.replace(/[_-]+\d{1,3}\s*$/, ""); // 3. `_`/`-`-delimited 1–3 digit suffix only
  s = s.replace(/_/g, " ").replace(/\s+/g, " ").trim(); // 4. underscores → spaces
  if (!s) return s;
  return s.split(" ").map(titleCaseWord).join(" "); // 5. title-case
};

// Splits the backend's per-row text into {author, document}. The
// current backend (`handler.py:documents_provided` branch) emits each
// row as `<author> | <file.name>` — a clean pipe-delimited split. Older
// stored rows (pre file-name change) used `<classification-derived
// prefix> from/of <file.author>` — those fall back to the legacy
// non-greedy " from "/" of "/" by " split so existing cases keep
// rendering correctly. When neither format matches, the whole string
// is treated as the author.
//
// The real-filename (piped) document is run through `cleanDocumentName`;
// the legacy prefix ("Records of") is already display-ready and left as-is.
const splitAuthor = (raw: string): { document: string; author: string } => {
  const trimmed = raw.trim();
  const piped = trimmed.split(/\s+\|\s+/);
  if (piped.length === 2) {
    return {
      author: piped[0].trim(),
      document: cleanDocumentName(piped[1].trim()),
    };
  }
  const legacy = trimmed.match(/^(.+?)\s+(from|of|by)\s+(.+)$/i);
  if (legacy) {
    return {
      document: `${legacy[1]} ${legacy[2]}`.trim(),
      author: legacy[3].trim(),
    };
  }
  return { document: "", author: trimmed };
};

// Rendered when the case has no doc-sorter files yet, in place of
// leaving a dangling preamble that promises a schedule below it.
const EMPTY_PLACEHOLDER_ROWS: readonly DocumentRow[] = [
  { author: "—", document: "(no documents on file)", date: "—" },
];

export type ParsedDocumentsSummary = {
  preamble: string;
  rows: DocumentRow[];
};

const normalise = (s: string): string =>
  s
    .toLowerCase()
    .replace(/:\s*$/, "")
    .replace(/[^a-z0-9\s]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

/** Parse the `summary_from_doc_sorter` text into (preamble, rows).
 *
 *  Group-header lines like `"Letter of Instruction:"` are dropped — the
 *  3-column table carries no category column. Anything before the first
 *  numbered/header line is treated as preamble prose and concatenated. */
export const parseDocumentsSummary = (
  summaryText: string
): ParsedDocumentsSummary => {
  const lines = (summaryText ?? "").split("\n");
  const preambleLines: string[] = [];
  const rows: DocumentRow[] = [];
  let seenBody = false;

  for (const raw of lines) {
    const line = raw.trim();
    if (!line) {
      if (!seenBody) preambleLines.push("");
      continue;
    }
    const match = line.match(NUMBERED_LINE_RE);
    if (match) {
      seenBody = true;
      const [, , /* num */ doc, date] = match;
      const { document, author } = splitAuthor(doc);
      rows.push({
        author,
        document,
        date: (date ?? "").trim(),
      });
      continue;
    }
    if (line.endsWith(":")) {
      // Group header — marks the body but is not itself a row.
      seenBody = true;
      continue;
    }
    if (!seenBody) preambleLines.push(line);
  }

  return {
    preamble: preambleLines.join("\n").trim(),
    rows,
  };
};

/** Case- and punctuation-insensitive alias match for a template heading. */
const matchesAlias = (text: string): boolean => {
  const norm = normalise(text);
  if (!norm) return false;
  return LIST_OF_DOCUMENTS_TEMPLATE_HEADING_ALIASES.some(
    (a) => normalise(a) === norm
  );
};

/** Case- and punctuation-insensitive substring match used for fallback
 *  anchor lookups. Mirrors `module-insert.ts:anchorMatches` so the
 *  caller can pass through the same preamble fallback list and get
 *  consistent positioning. */
const anchorTextMatches = (paragraphText: string, anchor: string): boolean => {
  const a = normalise(paragraphText);
  const b = normalise(anchor);
  if (!a || !b) return false;
  return a.includes(b);
};

type ParaInfo = {
  node: PMNode;
  text: string;
  from: number;
  to: number;
};

const collectParagraphs = (view: EditorView): ParaInfo[] => {
  const out: ParaInfo[] = [];
  view.state.doc.descendants((node, pos) => {
    if (node.type.name === "paragraph" || node.type.name === "doc_paragraph") {
      out.push({
        node,
        text: node.textContent,
        from: pos,
        to: pos + node.nodeSize,
      });
      return false;
    }
    return true;
  });
  return out;
};

const isSignaturePara = (text: string): boolean => SIGNATURE_RE.test(text);

/** Find a template paragraph matching one of the LoI heading aliases,
 *  along with whether its section already carries body content. Returns
 *  null when no alias matches.
 *
 *  `has_body_content` mirrors the backend's notion: any non-blank,
 *  non-heading paragraph between the matched heading and the next
 *  heading or signature counts as body. */
export const findTemplateHeading = (
  paragraphs: ParaInfo[],
  hints?: OutlineHints
): { index: number; hasBodyContent: boolean } | null => {
  const signatureIdx = paragraphs.findIndex((p) => isSignaturePara(p.text));
  const end = signatureIdx === -1 ? paragraphs.length : signatureIdx;

  for (let idx = 0; idx < end; idx++) {
    if (!matchesAlias(paragraphs[idx].text)) continue;
    let hasBody = false;
    for (let j = idx + 1; j < end; j++) {
      const p = paragraphs[j];
      if (!p.text.trim()) continue;
      if (isHeadingPara(p.node, hints)) break;
      hasBody = true;
      break;
    }
    return { index: idx, hasBodyContent: hasBody };
  }
  return null;
};

export type InsertDocumentsTableOptions = {
  /** Heading label to emit when no template heading is found. */
  headingLabel: string;
  /** Raw `summary_from_doc_sorter` text — the numbered document list. */
  summaryText: string;
  /** Optional expert commentary appended below the table. */
  expertsResponseText?: string | null;
  /** Backend outline hints (priority heading classification). */
  outlineHints?: OutlineHints;
  /** Fallback anchors to use when no template heading exists in the
   *  body (e.g. the VIC WorkSafe Psych IME template — module metadata
   *  has the docs section but the docx body skips it). Matched
   *  case- and punctuation-insensitively via substring containment;
   *  first match wins. Typically the same preamble anchors MDRS-97
   *  uses (`before: SERVICE STANDARDS` etc.) — that lands the docs
   *  section right after the 4 preamble headings instead of at the
   *  signature anchor. */
  fallbackAnchors?: ReadonlyArray<{
    position: "before" | "after";
    match: string;
  }>;
};

/** Insert the List-of-Documents table into the doc. Returns the number of
 *  top-level nodes inserted (0 when skipped: nothing to render, or the
 *  template already has body content under a matching heading).
 *
 *  Three branches mirror the backend MDRS-98 logic:
 *    • Template heading present + body content → skip entirely (preserve
 *      user / template content).
 *    • Template heading present + empty body → slot preamble + table +
 *      experts response directly under the preserved heading.
 *    • No template heading → emit heading + preamble + table + experts
 *      response at the standard insertion anchor (before signature; falls
 *      back to end-of-doc).
 *
 *  Idempotency: after the first fire the heading's section has body
 *  content (the inserted table), so subsequent calls take the skip
 *  branch and leave the doc alone. */
export const insertDocumentsTable = (
  view: EditorView,
  options: InsertDocumentsTableOptions
): number => {
  const summaryText = options.summaryText ?? "";
  const expertsText = (options.expertsResponseText ?? "").trim();
  const hasData = !!summaryText.trim() || !!expertsText;

  const schema = view.state.schema;
  const paragraphType = schema.nodes.paragraph ?? schema.nodes.doc_paragraph;
  const strongMark = schema.marks.strong ?? schema.marks.bold;
  if (!paragraphType) return 0;

  const tableType = schema.nodes.table ?? null;
  const tableRowType = schema.nodes.tableRow ?? null;
  const tableCellType = schema.nodes.tableCell ?? null;
  const hasTableNodes = !!(tableType && tableRowType && tableCellType);

  const paragraphs = collectParagraphs(view);
  const templateHeading = findTemplateHeading(paragraphs, options.outlineHints);

  if (templateHeading?.hasBodyContent) {
    // Template author / user already populated this section — preserve.
    return 0;
  }

  // Heading already present but empty, and no documents/commentary to add —
  // the heading requirement is already satisfied, so leave it untouched
  // (adding anything here would append a blank line on every re-run). When
  // the heading is ABSENT we still fall through to synthesise it (+ a
  // placeholder table) so every report carries the section regardless of
  // template or documents. (Always auto-insert.)
  if (templateHeading && !hasData) return 0;

  const { preamble, rows } = parseDocumentsSummary(summaryText);
  const expertsLines = expertsText
    .split("\n")
    .map((l) => l.trim())
    .filter((l) => l.length > 0);

  // Donors: when a template heading exists, clone its run marks for any
  // new heading we emit (we won't emit one in that branch, but the body
  // donor is the paragraph after the heading). When no template heading
  // exists, pick the first non-chrome heading paragraph in the doc as
  // the donor — same pattern as qta-insert.
  const sigIdx = paragraphs.findIndex((p) => isSignaturePara(p.text));
  const donorLimit = sigIdx === -1 ? paragraphs.length : sigIdx;

  let headingDonor: PMNode | null = null;
  let bodyDonor: PMNode | null = null;
  if (templateHeading) {
    headingDonor = paragraphs[templateHeading.index].node;
    bodyDonor = paragraphs[templateHeading.index + 1]?.node ?? null;
  } else {
    for (let i = 0; i < donorLimit; i++) {
      if (!isHeadingPara(paragraphs[i].node, options.outlineHints)) continue;
      headingDonor = paragraphs[i].node;
      bodyDonor = paragraphs[i + 1]?.node ?? null;
      break;
    }
  }

  const headingAttrs = visualParaAttrs(headingDonor);
  const bodyAttrs = visualParaAttrs(bodyDonor);
  const headingRunMarks = headingDonor
    ? firstRunMarks(headingDonor)
    : strongMark
      ? [strongMark.create()]
      : [];
  const bodyRunMarks = bodyDonor ? firstRunMarks(bodyDonor) : [];

  // Build the block of nodes to insert.
  const newNodes: PMNode[] = [];

  if (!templateHeading) {
    // Leading spacer + heading + breathing spacer (matches the QtA block
    // shape so the inserted section sits cleanly between its neighbours).
    newNodes.push(paragraphType.create(bodyAttrs, Fragment.empty));
    newNodes.push(
      paragraphType.create(
        headingAttrs,
        Fragment.from(schema.text(options.headingLabel, headingRunMarks))
      )
    );
    newNodes.push(paragraphType.create(bodyAttrs, Fragment.empty));
  } else if (preamble) {
    // Template kept its own heading paragraph; without this spacer the
    // preamble lands directly on the next line under the heading, which
    // reads cramped. The synthesized-heading branch above already emits
    // a trailing spacer, so it doesn't need this.
    newNodes.push(paragraphType.create(bodyAttrs, Fragment.empty));
  }

  if (preamble) {
    newNodes.push(
      paragraphType.create(
        bodyAttrs,
        Fragment.from(buildInlineNodes(schema, preamble, bodyRunMarks))
      )
    );
  }

  // Render the table when we have rows, or when the preamble promises a
  // schedule "below" and we need a placeholder to fill the gap. When the
  // section is being inserted with only an experts response (no preamble),
  // skip the table entirely — a "(no documents on file)" placeholder
  // above standalone commentary would be confusing.
  // Render the table when there are rows or a preamble, or when we're
  // synthesising the section with no data at all (heading-only would read as
  // an orphan — a "(no documents on file)" placeholder table is clearer).
  const shouldRenderTable =
    rows.length > 0 || preamble.length > 0 || (!templateHeading && !hasData);
  const tableRows: readonly DocumentRow[] =
    rows.length > 0 ? rows : EMPTY_PLACEHOLDER_ROWS;

  if (shouldRenderTable) {
    // Spacer between the preamble paragraph ("I confirm I received…")
    // and the table — without it the heading line of the table butts
    // straight against the preamble, which reads cramped.
    if (preamble) {
      newNodes.push(paragraphType.create(bodyAttrs, Fragment.empty));
    }
    if (hasTableNodes) {
      newNodes.push(buildPmTable(view, tableRows, bodyDonor, headingRunMarks));
      // Spacer after the table. Two purposes:
      //  • Visually separates the schedule from the experts response
      //    or the following section heading.
      //  • Gives the painter room to break before the page footer
      //    on a tall table — without it the last row tends to render
      //    on top of the running footer.
      newNodes.push(paragraphType.create(bodyAttrs, Fragment.empty));
    } else {
      // Text-row fallback for schemas without table nodes (tests, future
      // forks). Tab-separated so a copy-paste into Word still lays out.
      newNodes.push(
        paragraphType.create(
          bodyAttrs,
          Fragment.from(
            buildInlineNodes(
              schema,
              "Author\tName of document\tDate",
              headingRunMarks
            )
          )
        )
      );
      for (const row of tableRows) {
        const line = `${row.author}\t${row.document}\t${row.date}`;
        newNodes.push(
          paragraphType.create(
            bodyAttrs,
            Fragment.from(buildInlineNodes(schema, line, bodyRunMarks))
          )
        );
      }
    }
  }

  for (const line of expertsLines) {
    newNodes.push(
      paragraphType.create(
        bodyAttrs,
        Fragment.from(buildInlineNodes(schema, line, bodyRunMarks))
      )
    );
  }

  // Breathing space before the next heading — without this the final
  // content paragraph butts up against the following heading (e.g.
  // SERVICE STANDARDS), since insertion lands directly above it.
  newNodes.push(paragraphType.create(bodyAttrs, Fragment.empty));

  if (newNodes.length === 0) return 0;

  // Resolve insertion position:
  //   • Template heading present (no body) → directly after the heading.
  //   • No template heading → try the caller's fallbackAnchors (lands
  //     the synthesized section right after the 4 preamble headings,
  //     before SERVICE STANDARDS / whatever comes next).
  //   • No fallback match → before the signature.
  //   • No signature → end-of-doc.
  let anchorPos: number;
  if (templateHeading) {
    anchorPos = paragraphs[templateHeading.index].to;
  } else {
    let fallbackPos: number | null = null;
    if (options.fallbackAnchors) {
      for (const anchor of options.fallbackAnchors) {
        const idx = paragraphs.findIndex((p) =>
          anchorTextMatches(p.text, anchor.match)
        );
        if (idx === -1) continue;
        fallbackPos =
          anchor.position === "before" ? paragraphs[idx].from : paragraphs[idx].to;
        break;
      }
    }
    if (fallbackPos !== null) {
      anchorPos = fallbackPos;
    } else if (sigIdx !== -1) {
      anchorPos = paragraphs[sigIdx].from;
    } else {
      anchorPos = view.state.doc.content.size;
    }
  }

  const tr = view.state.tr.insert(anchorPos, Fragment.from(newNodes));
  view.dispatch(tr);
  return newNodes.length;
};

/** Build a 3-col `# | Document | Date` PM table node. Header cells are
 *  bolded (cloning the heading donor's marks + adding strong); data cells
 *  clone the body donor's paragraph + run marks. */
const buildPmTable = (
  view: EditorView,
  rows: readonly DocumentRow[],
  bodyDonor: PMNode | null,
  headerRunMarks: readonly import("prosemirror-model").Mark[]
): PMNode => {
  const schema = view.state.schema;
  const paragraphType = schema.nodes.paragraph ?? schema.nodes.doc_paragraph;
  const tableType = schema.nodes.table;
  const tableRowType = schema.nodes.tableRow;
  const tableCellType = schema.nodes.tableCell;
  const strongMark = schema.marks.strong ?? schema.marks.bold;

  const bodyAttrs = visualParaAttrs(bodyDonor);
  const bodyRunMarks = bodyDonor ? firstRunMarks(bodyDonor) : [];

  // Header marks: clone the heading donor's run marks and ensure strong
  // is present (the backend emits `cell_run.bold = True` regardless).
  const headerMarks = (() => {
    const base = [...headerRunMarks];
    const hasStrong = base.some(
      (m) => m.type.name === "strong" || m.type.name === "bold"
    );
    if (!hasStrong && strongMark) base.push(strongMark.create());
    return base;
  })();

  // Column widths in editor pixels. docx-editor follows the standard
  // prosemirror-tables convention: `colwidth: [N]` on each cell sizes
  // its column to N pixels at base zoom. Hand-picked split for the
  // 3-column Author | Name of document | Date layout:
  //   • `Author`            180px — fits long org/firm names (e.g.
  //                                  "Department of Health")
  //   • `Name of document`  300px — fits typical uploaded filenames
  //                                  with extension; long filenames
  //                                  wrap onto a second line
  //   • `Date`              100px — fits `dd/mm/yyyy`
  // Sums to 580px, close to the editable column width on A4 portrait
  // at the editor's typical fit-to-container zoom.
  const COLUMN_WIDTHS_PX = [180, 300, 100] as const;

  // 0.5pt black single-line border on every side of every cell — matches
  // Word's default table style. `size` is in eighths of a point per the
  // Eigenpal BorderSpec schema (4 = 0.5pt). Without this every cell renders
  // borderless and the table reads as four floating columns.
  const CELL_BORDER = { style: "single", color: "#000000", size: 4 } as const;
  const CELL_BORDERS = {
    top: CELL_BORDER,
    bottom: CELL_BORDER,
    left: CELL_BORDER,
    right: CELL_BORDER,
  } as const;

  // Cell margins in twips (1 twip = 1/20 of a point). 60 twips ≈ 3pt
  // vertical breathing room top/bottom; 120 twips ≈ 6pt horizontal
  // padding left/right. Roughly Word's default cell margins — without
  // them the text butts directly against the cell borders.
  const CELL_MARGINS = {
    top: 60,
    bottom: 60,
    left: 120,
    right: 120,
  } as const;

  const buildCell = (
    value: string,
    marks: readonly import("prosemirror-model").Mark[],
    colwidthPx: number
  ): PMNode => {
    const para = paragraphType.create(
      bodyAttrs,
      value ? Fragment.from(buildInlineNodes(schema, value, marks)) : Fragment.empty
    );
    return tableCellType.create(
      { colwidth: [colwidthPx], borders: CELL_BORDERS, margins: CELL_MARGINS },
      Fragment.from(para)
    );
  };

  const headerRow = tableRowType.create(
    null,
    Fragment.fromArray([
      buildCell("Author", headerMarks, COLUMN_WIDTHS_PX[0]),
      buildCell("Name of document", headerMarks, COLUMN_WIDTHS_PX[1]),
      buildCell("Date", headerMarks, COLUMN_WIDTHS_PX[2]),
    ])
  );

  const dataRows = rows.map((r) =>
    tableRowType.create(
      null,
      Fragment.fromArray([
        buildCell(r.author, bodyRunMarks, COLUMN_WIDTHS_PX[0]),
        buildCell(r.document, bodyRunMarks, COLUMN_WIDTHS_PX[1]),
        buildCell(r.date, bodyRunMarks, COLUMN_WIDTHS_PX[2]),
      ])
    )
  );

  return tableType.create(null, Fragment.fromArray([headerRow, ...dataRows]));
};
